import pluralize from 'pluralize'
import { useState } from 'react'

import { Button, Icon, cn } from '@hap/components'

import { useUploadContext } from './hooks'
import { ImportProgressDialog } from './import-progress-dialog'
import { ImportStageBar } from './import-stage-bar'

interface UploadPanelProps {
  variant?: 'compact' | 'full'
}

export function UploadPanel({ variant = 'compact' }: UploadPanelProps) {
  const { items, importProgress, isComplete, hasErrors } = useUploadContext()
  const [detailsOpen, setDetailsOpen] = useState(false)

  if (items.length === 0) return null

  const activeItems = items.filter(
    (item) => item.status !== 'done' && item.status !== 'error',
  )
  const uploadingItems = activeItems.filter((item) => item.status === 'uploading')
  const uploadProgress = uploadingItems.length
    ? Math.round(
        uploadingItems.reduce((total, item) => total + (item.uploadProgress ?? 0), 0) /
          uploadingItems.length,
      )
    : null
  const progressLabel =
    uploadProgress !== null
      ? `Uploading ${uploadProgress}%`
      : `${activeItems.length} of ${items.length} files processing`

  if (variant === 'compact') {
    return (
      <div className="space-y-2 p-3">
        {isComplete ? (
          <div className="flex items-center gap-2">
            <Icon name="check" className="size-4 text-green-600" />
            <span
              className={cn(
                'text-sm font-medium',
                hasErrors ? 'text-red-500' : 'text-green-600',
              )}
            >
              {hasErrors ? 'Complete with errors' : 'Uploads complete'}
            </span>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">{progressLabel}</span>
            </div>
            <ImportStageBar size="sm" />
          </>
        )}
      </div>
    )
  }

  // Full variant for setup/customize page
  return (
    <div className="outline-lime-green-light bg-lime-green/20 border-lime-green-dark mx-6 my-4 flex items-center gap-4 rounded-xl border p-4 outline-3">
      <h4 className="flex grow items-center text-lg font-medium">
        <span className="bg-lime-green border-lime-green-dark mr-2 size-3 shrink-0 rounded-full border"></span>
        <span>
          {isComplete
            ? hasErrors
              ? 'Imports complete with errors'
              : 'Imports complete'
            : `You have ${activeItems.length} ${pluralize('interaction', activeItems.length)} processing`}
        </span>
      </h4>
      <div className="flex min-w-44 flex-col gap-1">
        <div className="flex items-center justify-between gap-3 text-sm font-medium">
          <span>{importProgress.percent}% complete</span>
          <span className="text-muted-foreground text-xs font-normal">
            {importProgress.completed}/{importProgress.total}
          </span>
        </div>
        <div className="bg-background/70 border-lime-green-dark h-2 rounded-full border">
          <div
            className="bg-lime-green outline-lime-green-dark h-full rounded-full outline transition-all duration-500"
            style={{ width: `${importProgress.percent}%` }}
          />
        </div>
      </div>
      <div>
        <Button
          icon="eye"
          size="icon"
          variant="lime"
          onClick={() => setDetailsOpen(true)}
          aria-label="view import progress"
        />
      </div>
      <ImportProgressDialog open={detailsOpen} onOpenChange={setDetailsOpen} />
    </div>
  )
}
